import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiGet, apiPost } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { SearchSelect } from '@/components/composites/search-select'
import { paths } from '@/routes/paths'
import { useClientProjectsNav } from './api'

const base = (id: string) => `client-project-changes/${encodeURIComponent(id)}`

function useAfterChange() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: qk.projects() })
    void qc.invalidateQueries({ queryKey: qk.lifecycles() })
    void qc.invalidateQueries({ queryKey: ['crm-projects'] })
  }
}

/**
 * Merge this Client Project into another: the same account after all. It becomes an Additional
 * Requirements cycle there, its own extra projects, open tasks and CRM deals go with it, and its
 * lifecycle closes. It can be undone from the other project.
 */
export function MergeDialog({ projectId, name, onClose }: { projectId: string; name: string; onClose: () => void }) {
  const { workspaceSlug = '' } = useParams()
  const navigate = useNavigate()
  const done = useAfterChange()
  const options = (useClientProjectsNav(true).data ?? []).filter((p) => p.id !== projectId).map((p) => ({ id: p.id, label: p.label }))
  const [target, setTarget] = useState('')
  const run = useMutation({
    mutationFn: () => apiPost<{ ok: true; projectId: string }>(`${base(projectId)}/merge`, { targetId: target }),
    retry: false,
    onSuccess: (r) => {
      done()
      toast.success('Merged. Undo is on the other project if you need it.')
      onClose()
      navigate(paths.clientProject(workspaceSlug, r.projectId))
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
  return (
    <Dialog open onOpenChange={(o) => !o && !run.isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Merge into another Client Project</DialogTitle>
          <DialogDescription>
            Use this when “{name}” turns out to be part of another account. It becomes an Additional Requirements cycle of the project you pick, moving to that project's client and region. Its other projects, open tasks and CRM deals go with it, and its own dashboard closes. Nothing is deleted, and you can undo it from the other project.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-1.5">
          <Label>Merge into</Label>
          <SearchSelect value={target} onChange={setTarget} options={options} placeholder="Search Client Projects…" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={run.isPending}>Cancel</Button>
          <Button onClick={() => run.mutate()} disabled={!target || run.isPending}>{run.isPending ? 'Merging…' : 'Merge'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Undo the last conversion, CRM opening or merge of this Client Project, after saying what it will do. */
export function UndoDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { workspaceSlug = '' } = useParams()
  const navigate = useNavigate()
  const done = useAfterChange()
  const preview = useQuery({
    queryKey: ['client-project-changes', projectId, 'undo'],
    queryFn: () => apiGet<{ available: boolean; reason?: string; kind?: 'opened' | 'merged'; at?: string; summary?: string }>(`${base(projectId)}/undo`),
    retry: false,
  })
  const run = useMutation({
    mutationFn: () => apiPost<{ ok: true }>(`${base(projectId)}/undo`),
    retry: false,
    onSuccess: () => {
      done()
      toast.success('Undone')
      onClose()
      if (preview.data?.kind === 'merged') void preview.refetch()
      else navigate(paths.projects(workspaceSlug))
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
  const p = preview.data
  return (
    <Dialog open onOpenChange={(o) => !o && !run.isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Undo</DialogTitle>
          <DialogDescription>Undo the last conversion, opening from the CRM, or merge on this Client Project. Nothing is deleted.</DialogDescription>
        </DialogHeader>
        {preview.isPending ? (
          <p className="text-sm text-muted-foreground">Checking what can be undone…</p>
        ) : p?.available ? (
          <p className="text-sm">{p.summary}</p>
        ) : (
          <p className="text-sm text-muted-foreground">{p?.reason ?? 'Nothing to undo.'}</p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={run.isPending}>Cancel</Button>
          <Button variant="destructive" onClick={() => run.mutate()} disabled={!p?.available || run.isPending}>{run.isPending ? 'Undoing…' : 'Undo'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
